import { ProcessingConfig } from '../types';

export interface SavedPreset {
  id: string;
  name: string;
  config: ProcessingConfig;
  createdAt: Date;
  isBuiltIn?: boolean;
}

const PRESETS_STORAGE_KEY = 'ludwing_mastering_presets';

// Built-in presets
const BUILT_IN_PRESETS: SavedPreset[] = [
  {
    id: 'balanced',
    name: 'Balanced (Default)',
    isBuiltIn: true,
    createdAt: new Date(),
    config: {
      outputFormat: 'pcm24',
      genre: 'auto',
      useLimiter: true,
      normalize: true,
      outputGainDb: 0,
      transientPreservation: 50,
      useFrequencyWeighting: false,
      frequencyPreset: 'balanced',
      internalSampleRate: 48000,
    },
  },
  {
    id: 'transparent',
    name: 'Transparent (No Limiter)',
    isBuiltIn: true,
    createdAt: new Date(),
    config: {
      outputFormat: 'pcm24',
      genre: 'auto',
      useLimiter: false,
      normalize: false,
      outputGainDb: 0,
      transientPreservation: 0,
      useFrequencyWeighting: false,
      frequencyPreset: 'balanced',
      internalSampleRate: 48000,
    },
  },
  {
    id: 'punchy-rock',
    name: 'Punchy Rock',
    isBuiltIn: true,
    createdAt: new Date(),
    config: {
      outputFormat: 'pcm24',
      genre: 'rock',
      useLimiter: true,
      normalize: true,
      threshold: 0.94,
      outputGainDb: -0.3,
      transientPreservation: 10,
      useFrequencyWeighting: false,
      frequencyPreset: 'balanced',
      internalSampleRate: 48000,
    },
  },
  {
    id: 'loud-edm',
    name: 'Loud EDM',
    isBuiltIn: true,
    createdAt: new Date(),
    config: {
      outputFormat: 'pcm24',
      genre: 'edm',
      useLimiter: true,
      normalize: true,
      threshold: 0.96,
      outputGainDb: 0,
      transientPreservation: 5,  // Very low = heavily processed EDM sound
      useFrequencyWeighting: false,  // No post-EQ
      frequencyPreset: 'bright',
      internalSampleRate: 48000,
    },
  },
  {
    id: 'natural-acoustic',
    name: 'Natural Acoustic',
    isBuiltIn: true,
    createdAt: new Date(),
    config: {
      outputFormat: 'pcm24',
      genre: 'acoustic',
      useLimiter: true,
      normalize: true,
      threshold: 0.90,  // 90% = gentle limiting, not 50%
      outputGainDb: -1,
      transientPreservation: 30,  // Moderate preservation
      useFrequencyWeighting: true,  // Use warm EQ
      frequencyPreset: 'warm',
      internalSampleRate: 48000,
    },
  },
];

export class PresetManager {
  // Get all presets (built-in + user saved)
  static getAllPresets(): SavedPreset[] {
    const userPresets = this.getUserPresets();
    return [...BUILT_IN_PRESETS, ...userPresets];
  }

  // Get only user-saved presets
  static getUserPresets(): SavedPreset[] {
    try {
      const stored = localStorage.getItem(PRESETS_STORAGE_KEY);
      if (!stored) return [];
      const presets = JSON.parse(stored);
      // Convert date strings back to Date objects
      return presets.map((p: any) => ({
        ...p,
        createdAt: new Date(p.createdAt),
      }));
    } catch (error) {
      console.error('Error loading presets:', error);
      return [];
    }
  }

  // Save a new preset
  static savePreset(name: string, config: ProcessingConfig): SavedPreset {
    const userPresets = this.getUserPresets();

    const newPreset: SavedPreset = {
      id: `user_${Date.now()}`,
      name,
      config,
      createdAt: new Date(),
      isBuiltIn: false,
    };

    const updatedPresets = [...userPresets, newPreset];

    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));
      return newPreset;
    } catch (error) {
      console.error('Error saving preset:', error);
      throw new Error('Failed to save preset');
    }
  }

  // Delete a preset (only user presets)
  static deletePreset(id: string): void {
    const userPresets = this.getUserPresets();
    const updatedPresets = userPresets.filter(p => p.id !== id);

    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(updatedPresets));
    } catch (error) {
      console.error('Error deleting preset:', error);
      throw new Error('Failed to delete preset');
    }
  }

  // Get a specific preset by ID
  static getPreset(id: string): SavedPreset | undefined {
    return this.getAllPresets().find(p => p.id === id);
  }

  // Update an existing user preset
  static updatePreset(id: string, name: string, config: ProcessingConfig): void {
    const userPresets = this.getUserPresets();
    const index = userPresets.findIndex(p => p.id === id);

    if (index === -1) {
      throw new Error('Preset not found');
    }

    userPresets[index] = {
      ...userPresets[index],
      name,
      config,
    };

    try {
      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(userPresets));
    } catch (error) {
      console.error('Error updating preset:', error);
      throw new Error('Failed to update preset');
    }
  }

  // Export presets as JSON file
  static exportPresets(): string {
    const userPresets = this.getUserPresets();
    return JSON.stringify(userPresets, null, 2);
  }

  // Import presets from JSON
  static importPresets(jsonString: string): void {
    try {
      const importedPresets = JSON.parse(jsonString);
      const userPresets = this.getUserPresets();

      // Merge imported with existing, avoiding duplicates by name
      const merged = [...userPresets];
      importedPresets.forEach((imported: SavedPreset) => {
        if (!merged.find(p => p.name === imported.name)) {
          merged.push({
            ...imported,
            id: `user_${Date.now()}_${Math.random()}`,
            createdAt: new Date(),
            isBuiltIn: false,
          });
        }
      });

      localStorage.setItem(PRESETS_STORAGE_KEY, JSON.stringify(merged));
    } catch (error) {
      console.error('Error importing presets:', error);
      throw new Error('Failed to import presets');
    }
  }
}
